import { useTranslation } from 'react-i18next';
import type { ExerciseCompliance } from '@/api/treatmentPlans/treatmentPlansApi.types';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import { RecentExerciseSessions } from '@/components/common/RecentExerciseSessions';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import { usePatientExerciseComplianceQuery } from '@/hooks/useTreatmentPlan';

interface ExerciseComplianceSectionProps {
  patientId: string;
}

function resolveVideoStatusTone(videoStatus: string): BadgeTone {
  if (videoStatus === 'approved') {
    return 'success';
  }
  if (videoStatus === 'rejected') {
    return 'danger';
  }
  if (videoStatus === 'pending') {
    return 'warning';
  }
  return 'muted';
}

export function ExerciseComplianceSection({ patientId }: ExerciseComplianceSectionProps) {
  const { t } = useTranslation('plans');
  const complianceQuery = usePatientExerciseComplianceQuery(patientId);
  const rows = complianceQuery.data === undefined ? [] : complianceQuery.data;

  return (
    <RelatedRecordsSection
      title={t('editor.complianceTitle')}
      isLoading={complianceQuery.isLoading}
      isError={complianceQuery.isError}
      onRetry={function retry() {
        complianceQuery.refetch();
      }}
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zoomuted py-2">{t('editor.complianceEmpty')}</p>
      ) : (
        <div className="space-y-2">
          {rows.map(function renderRow(row: ExerciseCompliance) {
            return (
              <div key={row.id} className="p-3 border border-zooborder rounded-xl">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-zooindigo">{row.name}</p>
                  {row.videoStatus === 'none' ? null : (
                    <Badge tone={resolveVideoStatusTone(row.videoStatus)} size="sm">
                      {t('editor.complianceVideoStatus.' + row.videoStatus)}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge tone={row.totalCompletions > 0 ? 'success' : 'muted'} size="sm">
                    {row.totalCompletions > 0
                      ? t('editor.complianceDoneCount', { count: row.totalCompletions })
                      : t('editor.complianceNotStarted')}
                  </Badge>
                  {row.lastCompletedOn === null ? null : (
                    <span className="text-[11px] text-zoomuted">
                      {t('editor.complianceLastDone', {
                        date: formatIsoDateToDayAndMonth(row.lastCompletedOn),
                      })}
                    </span>
                  )}
                  {row.frequency === null ? null : (
                    <span className="text-[11px] text-zooviolet">{row.frequency}</span>
                  )}
                </div>
                <RecentExerciseSessions
                  title={t('editor.complianceRecentSessions')}
                  entries={row.recent}
                  isInitiallyOpen={row.recent.some(function hasNote(entry) {
                    return entry.note !== null;
                  })}
                />
              </div>
            );
          })}
        </div>
      )}
    </RelatedRecordsSection>
  );
}
